import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import api from '../services/api'
import TeamHelmet from '../components/TeamHelmet'
import UserAvatar from '../components/UserAvatar'
import './Dashboard.css'

interface LatestGameSummary {
  id: number
  homeTeam: string
  awayTeam: string
  homeScore: number
  awayScore: number
  playedAt: string | null
  uploadedAt: string
  player1User?: {
    id: number
    username: string
    displayName: string
    discordId: string | null
    discordAvatar: string | null
    teamAbbreviation: string
  }
  player2User?: {
    id: number
    username: string
    displayName: string
    discordId: string | null
    discordAvatar: string | null
    teamAbbreviation: string
  }
}

interface CurrentGoat {
  id: number
  username: string
  displayName: string
  wins: number
  losses: number
  ties: number
  winPercentage: number
  pointDifferential: number
  discordAvatar: string | null
  discordId: string | null
}

interface ProphetGamesStats {
  totalGames: number
  currentGoat: CurrentGoat | null
  recentGames: number
  p1Record: {
    wins: number
    losses: number
  }
  coinTossWinnerRecord: {
    wins: number
    losses: number
    ties: number
  }
  latestGame: LatestGameSummary | null
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<ProphetGamesStats | null>(null)
  const [latestGames, setLatestGames] = useState<LatestGameSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const recentGamesTrackRef = useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const totalGamesPlayed = stats?.totalGames ?? 0
  const totalScheduledGames = 132
  const progressPercent = Math.min(
    100,
    Math.round((totalGamesPlayed / totalScheduledGames) * 100)
  )
  const currentMvp = stats?.currentGoat ?? {
    id: 0,
    username: 'nostradamus',
    displayName: 'Nostradamus',
    wins: 0,
    losses: 0,
    ties: 0,
    winPercentage: 0,
    pointDifferential: 0,
    discordAvatar: null,
    discordId: null,
  }
  const mvpAvatarUrl =
    currentMvp.discordAvatar && currentMvp.discordId
      ? `https://cdn.discordapp.com/avatars/${currentMvp.discordId}/${currentMvp.discordAvatar}.png?size=96`
      : 'https://cdn.discordapp.com/embed/avatars/0.png'
  const showLatestGameArrows = latestGames.length > 2
  const p1Record = stats?.p1Record ?? { wins: 0, losses: 0 }
  const coinTossWinnerRecord = stats?.coinTossWinnerRecord ?? {
    wins: 0,
    losses: 0,
    ties: 0,
  }

  const updateLatestGamesScrollState = () => {
    const track = recentGamesTrackRef.current
    if (!track) {
      setCanScrollLeft(false)
      setCanScrollRight(false)
      return
    }

    const maxScrollLeft = track.scrollWidth - track.clientWidth
    setCanScrollLeft(track.scrollLeft > 4)
    setCanScrollRight(maxScrollLeft - track.scrollLeft > 4)
  }

  const scrollLatestGames = (direction: 'left' | 'right') => {
    const track = recentGamesTrackRef.current
    if (!track) {
      return
    }

    const scrollAmount = Math.max(track.clientWidth * 0.92, 280)
    track.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true)

      const [statsResult, gamesResult] = await Promise.allSettled([
        api.get<ProphetGamesStats>('/stats'),
        api.get<LatestGameSummary[]>('/games'),
      ])

      try {
        if (statsResult.status === 'fulfilled') {
          setStats(statsResult.value.data)
        } else {
          console.error('Failed to fetch stats:', statsResult.reason)
          setStats({
            totalGames: 0,
            currentGoat: null,
            recentGames: 0,
            p1Record: { wins: 0, losses: 0 },
            coinTossWinnerRecord: { wins: 0, losses: 0, ties: 0 },
            latestGame: null,
          })
        }

        if (gamesResult.status === 'fulfilled') {
          setLatestGames(gamesResult.value.data)
        } else {
          console.error('Failed to fetch latest games:', gamesResult.reason)
          setLatestGames([])
        }

        if (statsResult.status === 'fulfilled' && gamesResult.status === 'fulfilled') {
          setErrorMessage(null)
        } else {
          setErrorMessage('Unable to load some latest game information.')
        }
      } catch (error) {
        console.error('Failed to initialize dashboard:', error)
        setLatestGames([])
        setStats({
          totalGames: 0,
          currentGoat: null,
          recentGames: 0,
          p1Record: { wins: 0, losses: 0 },
          coinTossWinnerRecord: { wins: 0, losses: 0, ties: 0 },
          latestGame: null,
        })
        setErrorMessage('Unable to load latest statistics.')
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const renderLatestGameTeam = (
    teamAbbreviation: string,
    score: number,
    coach?: LatestGameSummary['player1User']
  ) => (
    <div className="team">
      <div className="team-info">
        <div className={coach ? 'coach-row' : 'team-only-row'}>
          {coach ? (
            <UserAvatar
              displayName={coach.displayName}
              discordId={coach.discordId}
              discordAvatar={coach.discordAvatar}
              size="sm"
              className="latest-game-avatar"
            />
          ) : null}
          <span className={coach ? 'coach-name' : 'team-only-name'}>
            {coach?.displayName ?? teamAbbreviation}
          </span>
        </div>
        <div className="team-bottom-row">
          <TeamHelmet
            abbreviation={teamAbbreviation}
            size="sm"
            className="team-helmet"
          />
          <span className="team-abbreviation">{teamAbbreviation}</span>
          <span className="team-score">{score}</span>
        </div>
      </div>
    </div>
  )

  const renderLatestGameCard = (game: LatestGameSummary) => (
    <div
      key={game.id}
      className="recent-game-card clickable"
      onClick={() => navigate(`/games/${game.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          navigate(`/games/${game.id}`)
        }
      }}
    >
      <div className="teams">
        {renderLatestGameTeam(
          game.homeTeam,
          game.homeScore,
          game.player1User
        )}
        <div className="vs">vs</div>
        {renderLatestGameTeam(
          game.awayTeam,
          game.awayScore,
          game.player2User
        )}
      </div>
      <div className="game-meta">
        <span>{formatDate(game.playedAt ?? game.uploadedAt)}</span>
      </div>
    </div>
  )

  useEffect(() => {
    updateLatestGamesScrollState()

    const handleResize = () => {
      updateLatestGamesScrollState()
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [latestGames.length])

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  return (
    <div className="dashboard">
      <section className="dashboard-hero">
        <div className="dashboard-hero-art dashboard-hero-logo-wrap">
          <img
            src="/prophet-games-main-logo.png"
            alt="The Prophet Games"
            className="dashboard-hero-logo"
          />
        </div>
      </section>
      <div className="dashboard-hero-links">
        <Link to="/competitors" className="btn btn-secondary">
          Competitors
        </Link>
        <Link to="/matchup-calls" className="btn btn-secondary">
          Matchup Call Tracker
        </Link>
        <Link to="/team-matchup-matrix" className="btn btn-secondary">
          Team Matchup Matrix
        </Link>
        <Link to="/team-performance" className="btn btn-secondary">
          Team Performance
        </Link>
      </div>
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Group Games Played</h3>
          <p className="stat-value">{totalGamesPlayed}</p>
          <div className="progress-meta">
            <span>{totalGamesPlayed} played</span>
            <span>{totalScheduledGames} total</span>
          </div>
          <div className="progress-track" aria-hidden="true">
            <div
              className="progress-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="progress-complete">{progressPercent}% Complete</p>
        </div>
        <div className="stat-card goat-card">
          <h3>Current Tourney MVP</h3>
          <div className="goat-info">
            <img
              src={mvpAvatarUrl}
              alt={currentMvp.displayName}
              className="goat-avatar"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                const fallback = e.currentTarget.nextElementSibling as HTMLElement | null
                if (fallback) fallback.style.display = 'flex'
              }}
            />
            <div className="goat-avatar-placeholder" style={{ display: 'none' }}>
              {currentMvp.displayName.charAt(0).toUpperCase()}
            </div>
            <div className="goat-details">
              <p className="goat-name">{currentMvp.displayName}</p>
              <p className="goat-record">
                {currentMvp.wins}-{currentMvp.losses}
                {currentMvp.ties > 0 ? `-${currentMvp.ties}` : ''}
                {' '}({(currentMvp.winPercentage * 100).toFixed(1)}%)
              </p>
              <p className="goat-differential">
                Point Differential {currentMvp.pointDifferential >= 0 ? '+' : ''}
                {currentMvp.pointDifferential}
              </p>
            </div>
          </div>
        </div>
        <div className="stat-card p1-record-card">
          <h3>P1 Record</h3>
          <p className="stat-value p1-record-value">
            {p1Record.wins}-{p1Record.losses}
          </p>
          <p className="stat-hint">Player 1 results across assigned games</p>
        </div>
        <div className="stat-card coin-toss-record-card">
          <h3>Coin Toss Winner Record</h3>
          <p className="stat-value coin-toss-record-value">
            {coinTossWinnerRecord.wins}-{coinTossWinnerRecord.losses}
            {coinTossWinnerRecord.ties > 0 ? `-${coinTossWinnerRecord.ties}` : ''}
          </p>
          <p className="stat-hint">Record for scheduled Home Team slots</p>
        </div>
      </div>

      <div className="recent-games-section">
        <h2>Latest Games</h2>
        <p className="section-hint">Click any game below to view details, or <Link to="/games" className="inline-link">view all games →</Link></p>
        {errorMessage && (
          <div className="message message-error">{errorMessage}</div>
        )}
        {latestGames.length > 0 ? (
          <div className={`recent-games-carousel${showLatestGameArrows ? '' : ' no-arrows'}`}>
            {showLatestGameArrows ? (
              <button
                type="button"
                className="recent-games-arrow recent-games-arrow-left"
                onClick={() => scrollLatestGames('left')}
                disabled={!canScrollLeft}
                aria-label="Scroll latest games left"
              >
                ‹
              </button>
            ) : null}
            <div
              ref={recentGamesTrackRef}
              className="recent-games-track"
              onScroll={updateLatestGamesScrollState}
            >
              {latestGames.map(renderLatestGameCard)}
            </div>
            {showLatestGameArrows ? (
              <button
                type="button"
                className="recent-games-arrow recent-games-arrow-right"
                onClick={() => scrollLatestGames('right')}
                disabled={!canScrollRight}
                aria-label="Scroll latest games right"
              >
                ›
              </button>
            ) : null}
          </div>
        ) : (
          <div className="recent-game-card empty">
            <p>No games have been uploaded yet.</p>
          </div>
        )}
      </div>

      <div className="actions">
        <Link to="/upload" className="btn btn-primary">
          Upload Game Results
        </Link>
        <Link to="/standings" className="btn btn-secondary">
          View Standings
        </Link>
        <Link to="/games" className="btn btn-secondary">
          Game Results
        </Link>
        <Link to="/rss" className="btn btn-secondary">
          RSS Game Result Feed
        </Link>
      </div>
    </div>
  )
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Unknown'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) {
    return 'Unknown'
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

